import { EmailConnectionRequiredBanner } from "components/EmailConnectionRequiredBanner";
import { Button } from "components/ui/Button";
import { Loading } from "components/ui/Loading/Loading";
import { useDeliveriesApi } from "hooks/use-delivery-api";
import {
  type DeliverySeries,
  useDeliverySeries,
} from "hooks/use-delivery-series-api";
import { useActiveGmailConnection } from "hooks/use-email-integrations-api";
import {
  type ReplyEmailItem,
  replyStatusDotClass,
  useReplies,
} from "hooks/use-email-replies-api";
import { cn } from "lib/utils";
import {
  ArrowLeft,
  Briefcase,
  ChevronRight,
  ExternalLink,
  Pencil,
  RefreshCw,
  User as UserIcon,
} from "lucide-react";
import { type FC, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { DeliverySeriesFormModal } from "routes/DeliverySeriesPage/components/DeliverySeriesFormModal";
import { DeliverySeriesStatusBadge } from "routes/DeliverySeriesPage/components/DeliverySeriesStatusBadge";
import { formatShortDateTime, timeAgo } from "utils/relative-time";

/**
 * 配信シリーズ詳細ページ.
 * @return {JSX.Element} ページ
 */
export const DeliverySeriesDetailPage: FC = () => {
  const { id } = useParams<{ id: string }>();
  const seriesId = id ?? null;
  const { series, loading, refetch } = useDeliverySeries(seriesId);
  const [editing, setEditing] = useState(false);

  if (!seriesId) return null;
  if (loading) {
    return (
      <div className="flex justify-center py-10">
        <Loading />
      </div>
    );
  }
  if (!series) {
    return (
      <div className="p-6 text-sm text-base-content/60">
        シリーズが見つかりません
        <div className="mt-3">
          <Link
            to="/delivery-series"
            className="text-primary hover:underline inline-flex items-center gap-1"
          >
            <ArrowLeft size={14} aria-hidden />
            一覧へ戻る
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6 max-w-6xl">
      <BackLink />
      <SeriesHeader series={series} onEdit={() => setEditing(true)} />
      <SummarySection series={series} />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <DeliveryHistorySection series={series} />
        <ReplyOverviewSection series={series} />
      </div>

      <DeliverySeriesFormModal
        isOpen={editing}
        mode="edit"
        series={series}
        onClose={() => setEditing(false)}
        onSaved={() => {
          setEditing(false);
          refetch();
        }}
      />
    </div>
  );
};

// ============================================================
// Sub-sections
// ============================================================

const BackLink: FC = () => (
  <Link
    to="/delivery-series"
    className="text-sm text-base-content/60 hover:text-base-content inline-flex items-center gap-1"
  >
    <ArrowLeft size={14} aria-hidden />
    シリーズ一覧へ
  </Link>
);

type SeriesHeaderProps = {
  series: DeliverySeries;
  onEdit: () => void;
};

const SeriesHeader: FC<SeriesHeaderProps> = ({ series, onEdit }) => {
  return (
    <header className="space-y-2">
      <div className="flex items-start gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-bold inline-flex items-center gap-2">
            {series.name}
            <DeliverySeriesStatusBadge status={series.status} />
          </h1>
          {series.description && (
            <p className="text-sm text-base-content/70 mt-1 whitespace-pre-line">
              {series.description}
            </p>
          )}
          {(series.talentName || series.focusMatterTitle) && (
            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-violet-700">
              {series.talentName && (
                <span className="inline-flex items-center gap-1">
                  <UserIcon size={12} aria-hidden />
                  タレント: {series.talentName}
                </span>
              )}
              {series.focusMatterTitle && (
                <span className="inline-flex items-center gap-1">
                  <Briefcase size={12} aria-hidden />
                  注力案件: {series.focusMatterTitle}
                </span>
              )}
            </div>
          )}
        </div>
        <Button type="button" size="sm" variant="outline" onClick={onEdit}>
          <span className="inline-flex items-center gap-1">
            <Pencil size={14} aria-hidden />
            編集
          </span>
        </Button>
      </div>
    </header>
  );
};

type SummarySectionProps = {
  series: DeliverySeries;
};

const SummarySection: FC<SummarySectionProps> = ({ series }) => {
  const stats = series.stats;
  const responseRate = Math.round(stats.responseRate * 100);
  return (
    <section className="rounded-lg border border-slate-200 bg-white shadow-sm p-4">
      <h2 className="text-sm font-semibold mb-3">サマリー</h2>
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-4">
        <Stat label="配信数" value={stats.deliveryCount} />
        <Stat label="累計宛先" value={stats.totalRecipients} />
        <Stat label="累計返信" value={stats.totalReplies} />
        <Stat label="未対応" value={stats.unresponded} tone="error" />
        <Stat label="対応中" value={stats.inProgress} tone="warning" />
        <Stat label="完了" value={stats.completed} tone="success" />
        <Stat label="対応率" value={`${responseRate}%`} tone="success" />
      </div>
      {stats.lastSentAt && (
        <div className="mt-3 text-xs text-base-content/60">
          最終配信: {formatShortDateTime(stats.lastSentAt)}
        </div>
      )}
    </section>
  );
};

type StatProps = {
  label: string;
  value: number | string;
  tone?: "error" | "warning" | "success";
};

const STAT_TONE_COLOR: Record<NonNullable<StatProps["tone"]>, string> = {
  error: "text-rose-600",
  warning: "text-amber-600",
  success: "text-emerald-600",
};

const Stat: FC<StatProps> = ({ label, value, tone }) => {
  const color = tone ? STAT_TONE_COLOR[tone] : "text-base-content";
  return (
    <div>
      <div className="text-xs text-base-content/60">{label}</div>
      <div className={cn("text-xl font-bold", color)}>{value}</div>
    </div>
  );
};

type DeliveryHistorySectionProps = {
  series: DeliverySeries;
};

const DeliveryHistorySection: FC<DeliveryHistorySectionProps> = ({
  series,
}) => {
  const navigate = useNavigate();
  const { data, loading } = useDeliveriesApi({
    seriesId: series.id,
    limit: 20,
  });
  const items = data?.items ?? [];
  const latestSentId = items.find(d => d.status === "sent")?.id ?? null;

  /**
   * 直近の配信を雛型に再配信フローへ
   * @return {void}
   */
  const handleResendLatest = (): void => {
    if (latestSentId) {
      navigate(`/deliveries/new?copy-from=${latestSentId}`);
    } else {
      navigate(`/deliveries/new?seriesId=${series.id}`);
    }
  };

  return (
    <section className="rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
        <h2 className="text-sm font-semibold">配信履歴</h2>
        <Button
          type="button"
          size="xs"
          variant="outline"
          onClick={handleResendLatest}
          disabled={series.status === "archived"}
          title={
            series.status === "archived"
              ? "アーカイブ済みシリーズには新規配信できません"
              : undefined
          }
        >
          <span className="inline-flex items-center gap-1">
            <RefreshCw size={12} aria-hidden />
            {latestSentId ? "再配信 (直近)" : "新規配信"}
          </span>
        </Button>
      </div>
      {loading ? (
        <div className="flex justify-center py-6">
          <Loading size="sm" />
        </div>
      ) : items.length === 0 ? (
        <div className="px-4 py-6 text-sm text-base-content/60">
          このシリーズにはまだ配信がありません
        </div>
      ) : (
        <ul>
          {items.slice(0, 10).map(d => (
            <li
              key={d.id}
              className="px-4 py-3 border-b border-slate-100 last:border-b-0 hover:bg-slate-50"
            >
              <Link
                to={`/deliveries/${d.id}`}
                className="flex items-center gap-2 text-sm"
              >
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">
                    {d.subject || "(件名なし)"}
                  </div>
                  <div className="text-xs text-base-content/60">
                    {d.sentAt
                      ? `配信 ${formatShortDateTime(d.sentAt)}`
                      : `下書き / 予約 (${d.status})`}{" "}
                    · {d.totalRecipients} 名宛
                  </div>
                </div>
                <ChevronRight
                  size={14}
                  className="text-base-content/40 shrink-0"
                  aria-hidden
                />
              </Link>
            </li>
          ))}
          {items.length > 10 && (
            <li className="px-4 py-2 text-xs text-base-content/50">
              …他 {items.length - 10} 件
            </li>
          )}
        </ul>
      )}
    </section>
  );
};

type ReplyOverviewSectionProps = {
  series: DeliverySeries;
};

const ReplyOverviewSection: FC<ReplyOverviewSectionProps> = ({ series }) => {
  const navigate = useNavigate();
  const { hasActiveConnection, loading: connectionLoading } =
    useActiveGmailConnection();
  const { items: replies, loading } = useReplies({
    seriesId: series.id,
    limit: 10,
  });

  return (
    <section className="rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden flex flex-col">
      <div className="px-4 py-3 border-b border-slate-200">
        <h2 className="text-sm font-semibold">
          リプライ一覧 (このシリーズ全体)
        </h2>
      </div>
      {!connectionLoading && !hasActiveConnection && (
        <div className="p-4">
          <EmailConnectionRequiredBanner message="Gmail を連携するとリプライが自動収集されます。" />
        </div>
      )}
      {loading ? (
        <div className="flex justify-center py-6">
          <Loading size="sm" />
        </div>
      ) : replies.length === 0 ? (
        <div className="px-4 py-6 text-sm text-base-content/60">
          このシリーズにはまだリプライがありません
        </div>
      ) : (
        <ul className="flex-1">
          {replies.slice(0, 6).map(r => (
            <ReplyOverviewRow key={r.id} reply={r} />
          ))}
        </ul>
      )}
      <div className="border-t border-slate-200 px-4 py-3">
        <Button
          type="button"
          size="sm"
          onClick={() => navigate(`/emails/replies?seriesId=${series.id}`)}
        >
          <span className="inline-flex items-center gap-1">
            <ExternalLink size={14} aria-hidden />
            リプライ管理で開く ({series.stats.totalReplies} 件)
          </span>
        </Button>
      </div>
    </section>
  );
};

type ReplyOverviewRowProps = {
  reply: ReplyEmailItem;
};

const ReplyOverviewRow: FC<ReplyOverviewRowProps> = ({ reply }) => {
  return (
    <li className="px-4 py-2 border-b border-slate-100 last:border-b-0">
      <div className="flex items-center gap-2">
        <span
          className={cn(
            "inline-block w-2 h-2 rounded-full shrink-0",
            replyStatusDotClass(reply.responseStatus),
          )}
          title={`status:${reply.responseStatus}`}
        />
        <span className="text-sm font-medium truncate">
          {reply.fromName || reply.fromEmail}
        </span>
        <span className="ml-auto text-xs text-base-content/50 shrink-0">
          {timeAgo(reply.receivedAt)}
        </span>
      </div>
      <div className="text-xs text-base-content truncate ml-4">
        {reply.subject || "(件名なし)"}
      </div>
    </li>
  );
};
